import { pick, compact, flatten, uniq } from 'lodash-es'
import { isNonEmptyArray } from '#app/lib/boolean_tests'
import { objectEntries } from '#app/lib/utils'
import { aggregateWorksClaims } from '#entities/components/lib/claims_helpers'
import { getEditionsWorks, getEntitiesByUris, type SerializedEntity } from '#entities/lib/entities'
import { getWorkAuthorsUris } from '#entities/lib/types/work_alt'
import type { ExtendedEntityType } from '#server/types/entity'

export async function fetchRelatedEntities (entities: SerializedEntity[], parentEntityType: ExtendedEntityType) {
  if (isSubentitiesTypeEdition(parentEntityType)) {
    const relatedEntities = await getEditionsWorks(entities)
    entities.forEach(pickAndAssignWorksClaims(relatedEntities))
  } else if (parentEntityType === 'human') {
    await addWorksAuthors(entities)
  }
}

const pickAndAssignWorksClaims = (relatedEntities: SerializedEntity[]) => (edition: SerializedEntity) => {
  const { claims } = edition
  const editionWorks = relatedEntities.filter(isEditionWork(claims))
  if (isNonEmptyArray(editionWorks)) {
    edition.claims = addWorksClaims(claims, editionWorks)
  }
}

export function addWorksClaims (claims: SerializedEntity['claims'], works: SerializedEntity[]) {
  // Use a new object to prevent mutating claims, allowing to use this function
  // in $derived without getting a state_unsafe_mutation error
  const extendedClaims = Object.assign({}, claims) as SerializedEntity['claims']
  const worksClaims = aggregateWorksClaims(works)
  for (const [ property, workPropertyClaims ] of objectEntries(worksClaims)) {
    if (!claims[property] && isNonEmptyArray(workPropertyClaims)) extendedClaims[property] = workPropertyClaims
  }
  return extendedClaims
}

const isEditionWork = (claims: SerializedEntity['claims']) => entity => claims['wdt:P629']?.includes(entity.uri)

export async function addWorksAuthors (works: SerializedEntity[]) {
  const authorsUris = uniq(compact(flatten(works.map(getWorkAuthorsUris))))
  const entities = await getEntitiesByUris({ uris: authorsUris })
  works.forEach(work => {
    const workAuthorUris = getWorkAuthorsUris(work)
    work.relatedEntities = pick(entities, workAuthorUris)
  })
}

const entitiesTypesWithSubentities = [ 'collection', 'publisher' ]

const isSubentitiesTypeEdition = type => entitiesTypesWithSubentities.includes(type)
